import React, { createContext, useContext, useEffect, useState, useCallback, useMemo } from 'react';
import api from '../services/api';
import { API_URL } from '../env';

const AuthContext = createContext(null);

const normalizeAuthUser = (payload) => {
  if (!payload || typeof payload !== 'object') return null;

  // Support backend response shapes:
  // 1) { user: { ...claims }, is_admin: bool }
  // 2) { ...claims, is_admin: bool }
  const claims = payload.user && typeof payload.user === 'object' ? payload.user : payload;
  const isAdmin = typeof payload.is_admin === 'boolean'
    ? payload.is_admin
    : typeof claims.is_admin === 'boolean'
      ? claims.is_admin
      : false;

  return {
    ...claims,
    is_admin: isAdmin,
    groups: Array.isArray(claims.groups) ? claims.groups : [],
    authenticated: claims.authenticated !== false,
  };
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const authBypassActive = process.env.REACT_APP_DEV_MODE_BYPASS_AUTH === 'true';
  const bypassUser = useMemo(
    () =>
      authBypassActive
        ? {
            email: 'devmode@example.com',
            name: 'Dev Mode Administrator',
            groups: ['admin'],
            is_admin: true,
            authenticated: true,
          }
        : null,
    [authBypassActive]
  );

  const fetchMe = useCallback(async () => {
    try {
      const { data } = await api.get('/api/auth/me');
      setUser(normalizeAuthUser(data));
    } catch (e) {
      if (e.response?.status === 401) {
        setUser(null);
      } else {
        console.error('Failed to fetch user:', e);
        setUser(null);
      }
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (authBypassActive && bypassUser) {
      setUser(bypassUser);
      setLoading(false);
      return;
    }
    fetchMe();
  }, [authBypassActive, bypassUser, fetchMe]);

  const login = () => {
    if (authBypassActive && bypassUser) {
      setUser(bypassUser);
      setLoading(false);
      return;
    }
    const redirectUri = window.location.href;
    window.location.href = `${API_URL}/api/auth/login?redirect_uri=${encodeURIComponent(redirectUri)}`;
  };

  const logout = async () => {
    if (authBypassActive && bypassUser) {
      setUser(bypassUser);
      setLoading(false);
      return;
    }
    try {
      await api.post('/api/auth/logout');
      setUser(null);
      window.location.href = '/';
    } catch (e) {
      console.error('Logout failed', e);
    }
  };

  return (
    <AuthContext.Provider value={{ user, loading, login, logout, authBypassActive }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => useContext(AuthContext);
